import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import StarsRoundedIcon from "@mui/icons-material/StarsRounded";
import VeersaPointsDashboardView from "./VeersaPointsDashboardView";
import { veersaPointsBannerSx, veersaPointsChipSx } from "./constants/veersaPointsFormUi";
import { VEERSA_POINTS_DRILLDOWN_EXCLUDED_ACTIVITY_CODES, VEERSA_POINTS_SELF_LOG_ACTIVITY_CODES, VEERSA_POINTS_EVIDENCE_HINTS } from "./constants/veersaPointsConstants";
import { getUserDetails } from "../../redux/actions";
import {
  getVeersaPointsActivities,
  getVeersaPointsLogActivities,
  getVeersaPointsDashboard,
  getVeersaPointsMySubmissions,
  postVeersaPointsEvidence,
  postVeersaPointsSubmission,
} from "../../http/requests/veersa-points";
import { toast } from "react-toastify";

const anchorCalendarYear = () => new Date().getFullYear();

const fyYearOptions = () => {
  const y = anchorCalendarYear();
  return [y - 2, y - 1, y, y + 1];
};

const ICON_BG = ["#ede9fe", "#dbeafe", "#d1fae5", "#fef3c7", "#fce7f3", "#e0e7ff", "#fee2e2"];

function mapBreakdownToActivities(breakdown) {
  if (!Array.isArray(breakdown)) return [];
  return breakdown.map((b, i) => {
    const used = Number(b.occurrencesUsed) || 0;
    const cap = Number(b.annualCapOccurrences) || 0;
    const earned = Number(b.pointsEarned) || 0;
    const remaining = Number(b.remainingPotentialPoints) || 0;
    const name = b.displayName || b.activityCode || "Activity";
    const activityCode = String(b.activityCode ?? b.ActivityCode ?? "").toUpperCase();
    return {
      id: String(b.activityId ?? i),
      name,
      activityCode,
      drillDownEnabled: !VEERSA_POINTS_DRILLDOWN_EXCLUDED_ACTIVITY_CODES.has(activityCode),
      meta: `${used} of ${cap} logged · ${remaining > 0 ? `${remaining} pts potential left` : "at cap"}`,
      pointsEarned: earned,
      color: { bg: ICON_BG[i % ICON_BG.length] },
      icon: String(name).charAt(0).toUpperCase() || "?",
    };
  });
}

/** Veersa Points — dashboard + log activity (POST /VeersaPoints/me/submissions). */
const VeersaPointsDashboard = () => {
  const dispatch = useDispatch();
  const { userData } = useSelector(({ USER }) => USER);

  const [openSubmit, setOpenSubmit] = useState(false);
  const [submitActivityId, setSubmitActivityId] = useState("");
  const [submitTitle, setSubmitTitle] = useState("");
  const [submitDescription, setSubmitDescription] = useState("");
  const [submitContentLink, setSubmitContentLink] = useState("");
  const [submitEvidenceFile, setSubmitEvidenceFile] = useState(null);
  const [submitAttachment, setSubmitAttachment] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [pointsYear, setPointsYear] = useState(() => anchorCalendarYear());
  const [dashboard, setDashboard] = useState(null);
  const [dashLoading, setDashLoading] = useState(false);
  const [mySubmissions, setMySubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);

  const yearOptions = useMemo(() => fyYearOptions(), []);

  const activeUser = userData?.data?.activeUsers;
  const employeeAzureId = activeUser?.id != null && activeUser?.id !== "" ? String(activeUser.id) : "";
  const nameFromProfile = [activeUser?.firstName, activeUser?.lastName].filter(Boolean).join(" ").trim();

  useEffect(() => {
    if (!userData?.data) {
      dispatch(getUserDetails());
    }
  }, [dispatch, userData]);

  const loadDashboard = useCallback(async () => {
    if (!employeeAzureId) return;
    setDashLoading(true);
    try {
      const data = await getVeersaPointsDashboard(pointsYear, employeeAzureId);
      setDashboard(data && typeof data === "object" ? data : null);
    } catch (err) {
      setDashboard(null);
      const body = err?.data;
      const msg =
        (typeof body?.errorMessage === "string" && body.errorMessage) ||
        (typeof body?.ErrorMessage === "string" && body.ErrorMessage) ||
        (typeof body?.message === "string" && body.message) ||
        err?.statusText ||
        "Could not load your Veersa Points dashboard.";
      toast.error(msg);
    } finally {
      setDashLoading(false);
    }
  }, [employeeAzureId, pointsYear]);

  const loadSubmissions = useCallback(async () => {
    if (!employeeAzureId) return;
    setSubmissionsLoading(true);
    try {
      const data = await getVeersaPointsMySubmissions(pointsYear);
      setMySubmissions(Array.isArray(data) ? data : []);
    } catch {
      setMySubmissions([]);
    } finally {
      setSubmissionsLoading(false);
    }
  }, [employeeAzureId, pointsYear]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  const [activityTypesForLog, setActivityTypesForLog] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let rows = await getVeersaPointsLogActivities().catch(() => getVeersaPointsActivities());
        if (cancelled || !Array.isArray(rows) || !rows.length) return;
        const mapped = rows
          .filter(
            (r) =>
              r.isActive !== false &&
              VEERSA_POINTS_SELF_LOG_ACTIVITY_CODES.has(String(r.activityCode || "").toUpperCase())
          )
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
          .map((r) => ({
            activityId: r.activityId,
            activityCode: String(r.activityCode || "").toUpperCase(),
            displayName: r.displayName,
            pointsPerOccurrence: Number(r.pointsPerOccurrence),
            maxOccurrencesPerYear: r.maxOccurrencesPerYear,
          }));
        if (mapped.length) setActivityTypesForLog(mapped);
      } catch {
        /* leave empty; user can retry via navigation */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedLogActivity = useMemo(
    () => activityTypesForLog.find((a) => String(a.activityId) === String(submitActivityId)),
    [activityTypesForLog, submitActivityId]
  );

  const resetSubmitForm = () => {
    setSubmitActivityId("");
    setSubmitTitle("");
    setSubmitDescription("");
    setSubmitContentLink("");
    setSubmitEvidenceFile(null);
    setSubmitAttachment(null);
  };

  const selectedActivityCode = selectedLogActivity?.activityCode || "";

  const canSubmitLog = useMemo(() => {
    if (!submitActivityId || !submitDescription.trim()) return false;
    const hasUploadedEvidence = Boolean(
      submitAttachment?.attachmentUrl || submitAttachment?.AttachmentUrl
    );
    const hasPendingFile = Boolean(submitEvidenceFile);
    if (selectedActivityCode === "CONTENT_CREATION") {
      return hasUploadedEvidence || hasPendingFile || Boolean(submitContentLink.trim());
    }
    if (selectedActivityCode === "CERTIFICATION" || selectedActivityCode === "MENTORING") {
      return hasUploadedEvidence || hasPendingFile;
    }
    return true;
  }, [
    submitActivityId,
    submitDescription,
    selectedActivityCode,
    submitAttachment,
    submitEvidenceFile,
    submitContentLink,
  ]);

  const handleSubmitLog = async () => {
    if (!submitActivityId || submitting || !canSubmitLog) return;
    setSubmitting(true);
    try {
      let attachment = submitAttachment;
      if (submitEvidenceFile && !attachment?.attachmentUrl) {
        const fd = new FormData();
        fd.append("file", submitEvidenceFile);
        attachment = await postVeersaPointsEvidence(fd);
        setSubmitAttachment(attachment);
      }

      const res = await postVeersaPointsSubmission({
        activityId: Number(submitActivityId),
        pointsYear,
        title: submitTitle.trim() || undefined,
        description: submitDescription.trim(),
        attachmentUrl: attachment?.attachmentUrl || attachment?.AttachmentUrl,
        attachmentFileName: attachment?.attachmentFileName || attachment?.AttachmentFileName,
        contentLink: submitContentLink.trim() || undefined,
      });
      toast.success(res?.message || "Veersa Points submission saved. It will appear after L&D review.");
      setOpenSubmit(false);
      resetSubmitForm();
      await loadDashboard();
      await loadSubmissions();
    } catch (err) {
      const body = err?.data;
      const msg =
        (typeof body?.errorMessage === "string" && body.errorMessage) ||
        (typeof body?.ErrorMessage === "string" && body.ErrorMessage) ||
        (typeof body?.message === "string" && body.message) ||
        err?.statusText ||
        "Could not save your submission.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const breakdown = dashboard?.breakdown ?? dashboard?.Breakdown;
  const activities = useMemo(() => mapBreakdownToActivities(breakdown), [breakdown]);

  const achieved = Number(dashboard?.achievedVeersaPoints ?? dashboard?.AchievedVeersaPoints) || 0;
  const cap = Number(dashboard?.annualVeersaPointsCap ?? dashboard?.AnnualVeersaPointsCap);
  const annualTarget = Number.isFinite(cap) && cap > 0 ? cap : Number(dashboard?.annualLearningHoursTarget ?? dashboard?.AnnualLearningHoursTarget) || 0;

  const currentMonth = Number(dashboard?.currentMonthApprovedPoints ?? dashboard?.CurrentMonthApprovedPoints) || 0;
  const previousMonth = Number(dashboard?.previousMonthApprovedPoints ?? dashboard?.PreviousMonthApprovedPoints) || 0;
  const monthlyDelta = currentMonth - previousMonth;

  const submissionsLogged = Number(dashboard?.submissionsLoggedCount ?? dashboard?.SubmissionsLoggedCount) || 0;
  const pending = Number(dashboard?.pendingSubmissionsCount ?? dashboard?.PendingSubmissionsCount) || 0;

  const categoriesCount = Array.isArray(breakdown) ? breakdown.filter((b) => (Number(b.occurrencesUsed) || 0) > 0).length : 0;

  const headerName =
    (dashboard?.employeeName || dashboard?.EmployeeName || "").trim() || nameFromProfile || "";

  return (
    <>
      <VeersaPointsDashboardView
        loading={dashLoading}
        employeeName={headerName}
        employeeAzureId={employeeAzureId}
        pointsYear={pointsYear}
        yearOptions={yearOptions}
        onPointsYearChange={setPointsYear}
        totalPoints={dashboard ? achieved : 0}
        annualTarget={dashboard ? annualTarget : 0}
        monthlyPoints={dashboard ? currentMonth : 0}
        monthlyDelta={dashboard ? monthlyDelta : 0}
        activitiesLogged={dashboard ? submissionsLogged : 0}
        categoriesCount={dashboard ? categoriesCount : 0}
        pendingSubmissions={dashboard ? pending : 0}
        activities={activities}
        mySubmissions={mySubmissions}
        submissionsLoading={submissionsLoading}
        onLogActivity={activityTypesForLog.length ? () => setOpenSubmit(true) : undefined}
      />

      <Dialog
        open={openSubmit}
        onClose={() => setOpenSubmit(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            boxSizing: "border-box",
            width: "100%",
            maxWidth: {
              xs: "calc(100vw - 2rem)",
              sm: "min(26rem, calc(100vw - 2rem))",
              lg: "min(28rem, calc(100vw - 22rem))",
            },
            m: { xs: 2, sm: 2 },
          },
        }}
        sx={{
          "& .MuiDialog-container": {
            alignItems: "center",
            justifyContent: "center",
          },
          "@media (min-width: 1024px)": {
            "& .MuiDialog-container": {
              justifyContent: "flex-start",
              paddingLeft: "20.5rem",
              paddingRight: "1.875rem",
            },
          },
        }}
      >
        <DialogTitle sx={{ pr: 6, pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" useFlexGap>
            <StarsRoundedIcon sx={{ color: "#5b21b6", fontSize: 28 }} aria-hidden />
            <Typography component="span" variant="h6" sx={{ fontWeight: 700 }}>
              Log a new activity
            </Typography>
            <Chip label="Veersa Points" size="small" sx={{ ...veersaPointsChipSx, height: 26 }} />
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box sx={veersaPointsBannerSx}>
            <StarsRoundedIcon sx={{ color: "#6d28d9", mt: 0.25 }} fontSize="small" aria-hidden />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#4c1d95", mb: 0.5 }}>
                Points are shown for each activity
              </Typography>
              <Typography variant="body2" sx={{ color: "#5b21b6", lineHeight: 1.5 }}>
                Approved submissions earn Veersa Points toward your annual goal. Each option below shows how many points
                you can earn per occurrence and your yearly cap.
              </Typography>
            </Box>
          </Box>

          <FormControl fullWidth margin="dense">
            <InputLabel id="vp-mock-act">Activity type</InputLabel>
            <Select
              labelId="vp-mock-act"
              label="Activity type"
              value={submitActivityId}
              onChange={(e) => setSubmitActivityId(e.target.value)}
              renderValue={(id) => {
                const a = activityTypesForLog.find((x) => String(x.activityId) === String(id));
                if (!a) return "";
                return (
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%", minWidth: 0 }}>
                    <Typography variant="body2" sx={{ flex: 1, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {a.displayName}
                    </Typography>
                    <Chip label={`+${a.pointsPerOccurrence} pts`} size="small" sx={veersaPointsChipSx} />
                  </Stack>
                );
              }}
            >
              {activityTypesForLog.map((a) => {
                const annualCapPts = a.pointsPerOccurrence * a.maxOccurrencesPerYear;
                return (
                  <MenuItem key={a.activityId} value={String(a.activityId)} sx={{ alignItems: "flex-start", py: 1.25, gap: 1 }}>
                    <StarsRoundedIcon sx={{ color: "#7c3aed", mt: 0.35, flexShrink: 0 }} fontSize="small" aria-hidden />
                    <ListItemText
                      primary={a.displayName}
                      secondary={`Up to ${annualCapPts} pts/yr · max ${a.maxOccurrencesPerYear} occurrence${a.maxOccurrencesPerYear === 1 ? "" : "s"}`}
                      primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                      secondaryTypographyProps={{ variant: "caption", sx: { color: "#6b7280" } }}
                      sx={{ flex: 1, minWidth: 0, my: 0 }}
                    />
                    <Chip label={`+${a.pointsPerOccurrence}`} size="small" sx={veersaPointsChipSx} />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {selectedLogActivity && (
            <Box
              sx={{
                mt: 1.5,
                mb: 0.5,
                p: 1.5,
                borderRadius: 1,
                bgcolor: "#faf5ff",
                border: "2px solid #a78bfa",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#4c1d95" }}>
                Selected activity
              </Typography>
              <Chip
                label={`+${selectedLogActivity.pointsPerOccurrence} Veersa Points per submission`}
                sx={{ ...veersaPointsChipSx, fontSize: "0.8125rem", height: 30 }}
              />
            </Box>
          )}

          <TextField margin="normal" fullWidth label="Title (optional)" value={submitTitle} onChange={(e) => setSubmitTitle(e.target.value)} />
          <TextField
            margin="normal"
            fullWidth
            multiline
            minRows={2}
            required
            label="Notes"
            helperText="Required — include context L&D needs to review your submission."
            value={submitDescription}
            onChange={(e) => setSubmitDescription(e.target.value)}
          />

          {selectedActivityCode && VEERSA_POINTS_EVIDENCE_HINTS[selectedActivityCode] ? (
            <Typography variant="caption" sx={{ display: "block", mt: 1.5, color: "#5b21b6" }}>
              {VEERSA_POINTS_EVIDENCE_HINTS[selectedActivityCode]}
            </Typography>
          ) : null}

          {(selectedActivityCode === "CERTIFICATION" ||
            selectedActivityCode === "MENTORING" ||
            selectedActivityCode === "CONTENT_CREATION") && (
            <Box sx={{ mt: 1.5 }}>
              <Button variant="outlined" component="label" size="small" sx={{ textTransform: "none" }}>
                Upload supporting evidence
                <input
                  type="file"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    setSubmitEvidenceFile(f);
                    setSubmitAttachment(null);
                  }}
                />
              </Button>
              {(submitEvidenceFile || submitAttachment?.attachmentFileName) && (
                <Typography variant="body2" sx={{ mt: 1, color: "#374151" }}>
                  {submitEvidenceFile?.name || submitAttachment?.attachmentFileName || submitAttachment?.AttachmentFileName}
                </Typography>
              )}
            </Box>
          )}

          {selectedActivityCode === "CONTENT_CREATION" && (
            <TextField
              margin="normal"
              fullWidth
              label="Content link (optional if file uploaded)"
              placeholder="https://..."
              value={submitContentLink}
              onChange={(e) => setSubmitContentLink(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ "& .MuiButton-contained": { backgroundColor: "#3C3489" } }}>
          <Button onClick={() => setOpenSubmit(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!canSubmitLog || submitting}
            onClick={handleSubmitLog}
          >
            {submitting ? "Saving…" : "Continue"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VeersaPointsDashboard;
